// MemberDashboard.tsx - Dashboard principal responsive
import React from 'react';
import { MembershipLevel, MembershipPermissions } from '../../types/adhesion.types';
import {
    Trophy,
    Users,
    FileText,
    ArrowUp,
    Star,
    Shield,
    User,
    Calendar,
    Bell,
    MapPin
} from 'lucide-react';

interface MemberDashboardProps {
    memberData: any;
    permissions: MembershipPermissions;
    membershipLevel: MembershipLevel;
    onSectionChange: (section: 'dashboard' | 'formation' | 'volunteer' | 'surveys' | 'projects' | 'profile') => void;
}

const MemberDashboard: React.FC<MemberDashboardProps> = ({
    memberData,
    permissions,
    membershipLevel,
    onSectionChange
}) => {
    // Obtenir le label du niveau
    const getLevelLabel = () => {
        switch (membershipLevel) {
            case 'full_member': return 'Membre Complet';
            case 'partial_member': return 'Membre Partiel';
            case 'sympathisant': return 'Sympathisant';
            default: return 'Membre';
        }
    };

    // Obtenir la couleur du niveau
    const getLevelColor = () => {
        switch (membershipLevel) {
            case 'full_member': return 'bg-green-50 text-green-700 border-green-200';
            case 'partial_member': return 'bg-yellow-50 text-yellow-700 border-yellow-200';
            case 'sympathisant': return 'bg-blue-50 text-blue-700 border-blue-200';
            default: return 'bg-gray-50 text-gray-700 border-gray-200';
        }
    };

    // Obtenir l'icône du niveau
    const getLevelIcon = () => {
        switch (membershipLevel) {
            case 'full_member': return <Shield className="w-5 h-5" />;
            case 'partial_member': return <Star className="w-5 h-5" />;
            case 'sympathisant': return <User className="w-5 h-5" />;
            default: return <User className="w-5 h-5" />;
        }
    };

    // Actions rapides disponibles selon les permissions
    const getQuickActions = () => {
        const actions = [];

        // Toujours afficher le profil qui contient carte, badges, parrainages, documents
        actions.push({
            id: 'profile',
            label: 'Mon profil complet',
            description: 'Carte, badges, documents, parrainages',
            icon: <User className="w-6 h-6 text-blue-600" />,
            color: 'bg-blue-50 hover:bg-blue-100 border-blue-200'
        });

        // Ajouter les espaces d'engagement
        actions.push(
            {
                id: 'formation',
                label: 'Formation',
                description: 'Développez vos compétences',
                icon: <Trophy className="w-6 h-6 text-green-600" />,
                color: 'bg-green-50 hover:bg-green-100 border-green-200'
            },
            {
                id: 'volunteer',
                label: 'Bénévolat',
                description: 'Participez aux actions',
                icon: <Users className="w-6 h-6 text-red-600" />,
                color: 'bg-red-50 hover:bg-red-100 border-red-200'
            },
            {
                id: 'surveys',
                label: 'Enquêtes',
                description: 'Donnez votre opinion',
                icon: <FileText className="w-6 h-6 text-purple-600" />,
                color: 'bg-purple-50 hover:bg-purple-100 border-purple-200'
            },
            {
                id: 'projects',
                label: 'Projets',
                description: 'Soutenez les initiatives',
                icon: <Star className="w-6 h-6 text-orange-600" />,
                color: 'bg-orange-50 hover:bg-orange-100 border-orange-200'
            }
        );

        return actions;
    };

    const quickActions = getQuickActions();

    return (
        <div className="space-y-6">
            {/* Header de bienvenue */}
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">
                            Bonjour {memberData.first_name} ! 👋
                        </h1>
                        <p className="text-gray-600 mt-1">
                            Bienvenue dans votre espace membre UDB
                        </p>
                    </div>
                    <div className={`mt-3 sm:mt-0 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getLevelColor()}`}>
                        {getLevelIcon()}
                        <span className="ml-2">{getLevelLabel()}</span>
                    </div>
                </div>
            </div>

            {/* Statistiques rapides */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                    <div className="flex items-center">
                        <div className="p-2 bg-blue-100 rounded-lg">
                            <Calendar className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="ml-3">
                            <p className="text-sm font-medium text-gray-600">Membre depuis</p>
                            <p className="text-lg font-semibold text-gray-900">
                                {new Date(memberData.created_at).toLocaleDateString('fr-FR', { 
                                    month: 'short', 
                                    year: 'numeric' 
                                })}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                    <div className="flex items-center">
                        <div className="p-2 bg-green-100 rounded-lg">
                            <Users className="w-5 h-5 text-green-600" />
                        </div>
                        <div className="ml-3">
                            <p className="text-sm font-medium text-gray-600">Parrainages</p>
                            <p className="text-lg font-semibold text-gray-900">
                                {memberData.referrals_count || 0}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                    <div className="flex items-center">
                        <div className="p-2 bg-orange-100 rounded-lg">
                            <Trophy className="w-5 h-5 text-orange-600" />
                        </div>
                        <div className="ml-3">
                            <p className="text-sm font-medium text-gray-600">Badges</p>
                            <p className="text-lg font-semibold text-gray-900">
                                {memberData.badges?.length || 0}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                    <div className="flex items-center">
                        <div className="p-2 bg-purple-100 rounded-lg">
                            <MapPin className="w-5 h-5 text-purple-600" />
                        </div>
                        <div className="ml-3">
                            <p className="text-sm font-medium text-gray-600">Localisation</p>
                            <p className="text-lg font-semibold text-gray-900">
                                {memberData.city}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Actions rapides */}
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Actions rapides</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {quickActions.map((action) => (
                        <button
                            key={action.id}
                            onClick={() => onSectionChange(action.id as 'dashboard' | 'formation' | 'volunteer' | 'surveys' | 'projects' | 'profile')}
                            className={`p-4 rounded-lg border-2 border-dashed transition-all duration-200 ${action.color}`}
                        >
                            <div className="flex flex-col items-center text-center space-y-2">
                                {action.icon}
                                <h3 className="font-medium text-sm">{action.label}</h3>
                                <p className="text-xs text-gray-600">{action.description}</p>
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            {/* Message d'amélioration si pas membre complet */}
            {membershipLevel !== 'full_member' && (
                <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-lg p-6 border border-blue-200">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-start space-x-3">
                            <div className="p-2 bg-blue-100 rounded-lg">
                                <ArrowUp className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">
                                    Devenez membre complet !
                                </h3>
                                <p className="text-gray-600 mt-1">
                                    {membershipLevel === 'sympathisant' 
                                        ? "Obtenez votre NIP pour accéder à tous les privilèges de membre"
                                        : "Complétez votre dossier pour débloquer votre carte de membre"
                                    }
                                </p>
                                <ul className="text-sm text-gray-600 mt-2 space-y-1">
                                    {membershipLevel === 'sympathisant' ? (
                                        <>
                                            <li>• Carte de membre officielle</li>
                                            <li>• Droit de vote aux assemblées</li>
                                            <li>• Accès aux documents privés</li>
                                        </>
                                    ) : (
                                        <>
                                            <li>• Carte de membre physique</li>
                                            <li>• QR code personnel</li>
                                            <li>• Privilèges complets</li>
                                        </>
                                    )}
                                </ul>
                            </div>
                        </div>
                        <button
                            onClick={() => onSectionChange('profile')}
                            className="mt-4 sm:mt-0 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                        >
                            En savoir plus
                        </button>
                    </div>
                </div>
            )}

            {/* Activité récente */}
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">Activité récente</h2>
                    <Bell className="w-5 h-5 text-gray-400" />
                </div>
                <div className="space-y-3">
                    <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                        <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                        <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">Adhésion validée</p>
                            <p className="text-xs text-gray-500">
                                {new Date(memberData.created_at).toLocaleDateString('fr-FR')}
                            </p>
                        </div>
                    </div>
                    
                    {memberData.referrals_count > 0 && (
                        <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                            <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                            <div className="flex-1">
                                <p className="text-sm font-medium text-gray-900">
                                    {memberData.referrals_count} parrainage{memberData.referrals_count > 1 ? 's' : ''} effectué{memberData.referrals_count > 1 ? 's' : ''}
                                </p>
                                <p className="text-xs text-gray-500">Contribution au mouvement</p>
                            </div>
                        </div>
                    )}

                    {memberData.badges?.length > 0 && (
                        <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                            <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                            <div className="flex-1">
                                <p className="text-sm font-medium text-gray-900">
                                    Badge{memberData.badges.length > 1 ? 's' : ''} débloqué{memberData.badges.length > 1 ? 's' : ''}
                                </p>
                                <p className="text-xs text-gray-500">Récompenses obtenues</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Informations importantes */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                    <Bell className="w-5 h-5 text-yellow-600 mt-0.5" />
                    <div>
                        <h3 className="text-sm font-semibold text-yellow-800">
                            Élections à venir
                        </h3>
                        <p className="text-sm text-yellow-700 mt-1">
                            Les élections législatives et locales auront lieu dans 2 semaines. 
                            Restez informé et mobilisez-vous !
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MemberDashboard;